import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  Alert,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Schedule as ScheduleIcon,
  Group as GroupIcon,
  Today as TodayIcon,
  Book as BookIcon,
  Person as PersonIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import { SubjectService } from '../../services/subjectService';
import { AttendanceService } from '../../services/attendanceService';
import { Subject, Student, AttendanceRecord } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

interface AttendanceRecordData {
  studentId: string;
  present: boolean;
}

export function SubjectAttendanceMarking() {
  const { user } = useAuth();
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecordData[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [confirmDialog, setConfirmDialog] = useState(false);

  useEffect(() => {
    loadAssignedSubjects();
  }, []);

  const loadAssignedSubjects = async () => {
    try {
      setLoading(true);
      // Get all subjects (would normally filter by faculty assignment)
      const result = await SubjectService.getSubjects(100);
      setSubjects(result.data);
    } catch (error) {
      console.error('Error loading subjects:', error);
      showSnackbar('Failed to load subjects', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubjectChange = (subjectId: string) => {
    setSelectedSubject(subjectId);
    const selectedSubjectData = subjects.find(s => s.id === subjectId);
    if (selectedSubjectData) {
      // Initialize attendance records for all students in the subject
      const records = selectedSubjectData.students.map(student => ({
        studentId: student.id,
        present: false
      }));
      setAttendanceRecords(records);
    }
  };

  const handleAttendanceChange = (studentId: string, present: boolean) => {
    setAttendanceRecords(prev => 
      prev.map(record => 
        record.studentId === studentId 
          ? { ...record, present }
          : record
      )
    );
  };

  const handleMarkAllPresent = () => {
    setAttendanceRecords(prev => prev.map(record => ({ ...record, present: true })));
  };

  const handleMarkAllAbsent = () => {
    setAttendanceRecords(prev => prev.map(record => ({ ...record, present: false })));
  };

  const handleSaveAttendance = () => {
    setConfirmDialog(true);
  };

  const confirmSaveAttendance = async () => {
    try {
      setSaving(true);
      setConfirmDialog(false);
      
      const selectedSubjectData = subjects.find(s => s.id === selectedSubject);
      if (!selectedSubjectData) {
        throw new Error('Selected subject not found');
      }

      // Create attendance records for each student
      const attendanceData = attendanceRecords.map(record => {
        const student = selectedSubjectData.students.find(s => s.id === record.studentId);
        return {
          studentId: record.studentId,
          studentName: student?.name || '',
          studentRollNumber: student?.rollNumber || '',
          classId: (student as any)?.originalClassId || '',
          className: (student as any)?.originalClassName || '',
          subjectId: selectedSubject,
          subjectName: selectedSubjectData.name,
          subjectCode: selectedSubjectData.code,
          date: new Date(selectedDate),
          isPresent: record.present,
          markedBy: user?.id || '',
          markedByName: user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.username || '',
          markedAt: new Date(),
        };
      });

      // Save attendance directly to subjects collection
      await AttendanceService.saveSubjectAttendanceToSubjectsCollection(
        selectedSubject,
        selectedDate,
        attendanceRecords.map(record => {
          const student = selectedSubjectData.students.find(s => s.id === record.studentId);
          return {
            studentId: record.studentId,
            studentName: student?.name || '',
            rollNumber: student?.rollNumber || '',
            isPresent: record.present,
            remarks: ''
          };
        }),
        user?.id || '',
        user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.username || ''
      );
      
      showSnackbar('Attendance saved successfully to subjects collection', 'success');
      
      // Reset form
      setSelectedSubject('');
      setAttendanceRecords([]);
      setSelectedDate(new Date().toISOString().split('T')[0]);
      
    } catch (error) {
      console.error('Error saving attendance:', error);
      showSnackbar('Failed to save attendance', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const selectedSubjectData = subjects.find(s => s.id === selectedSubject);
  const presentCount = attendanceRecords.filter(r => r.present).length;
  const totalCount = attendanceRecords.length;

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
        Subject Attendance Marking
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TodayIcon color="primary" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h6">Today</Typography>
                  <Typography color="textSecondary">
                    {new Date().toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <BookIcon color="info" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h6">Subject</Typography>
                  <Typography color="textSecondary">
                    {selectedSubjectData?.code || 'None Selected'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <GroupIcon color="success" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h6">Present</Typography>
                  <Typography color="textSecondary">
                    {presentCount} / {totalCount}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ScheduleIcon color="warning" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h6">Attendance Rate</Typography>
                  <Typography color="textSecondary">
                    {totalCount > 0 ? Math.round((presentCount / totalCount) * 100) : 0}%
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Selection Controls */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Select Subject and Date
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Subject</InputLabel>
              <Select
                value={selectedSubject}
                label="Subject"
                onChange={(e) => handleSubjectChange(e.target.value)}
                disabled={loading}
              >
                <MenuItem value="">
                  <em>Select a subject</em>
                </MenuItem>
                {subjects.map((subject) => (
                  <MenuItem key={subject.id} value={subject.id}>
                    <Box>
                      <Typography variant="body1">{subject.name}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {subject.code} • {subject.students.length} students
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={{
                  padding: '16.5px 14px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '16px',
                  width: '100%',
                  fontFamily: 'inherit',
                }}
              />
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={5}>
            <Box sx={{ display: 'flex', gap: 1, height: '100%', alignItems: 'center' }}>
              <Button
                variant="outlined"
                onClick={handleMarkAllPresent}
                disabled={!selectedSubject || attendanceRecords.length === 0}
                startIcon={<CheckIcon />}
              >
                Mark All Present
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={handleMarkAllAbsent}
                disabled={!selectedSubject || attendanceRecords.length === 0}
                startIcon={<CloseIcon />}
              >
                Mark All Absent
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveAttendance}
                disabled={!selectedSubject || attendanceRecords.length === 0 || saving}
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {saving ? 'Saving...' : 'Save Attendance'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Student List */}
      {selectedSubjectData && attendanceRecords.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Students in {selectedSubjectData.name} ({selectedSubjectData.code})
          </Typography>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Student</TableCell>
                  <TableCell>Roll Number</TableCell>
                  <TableCell>Original Class</TableCell>
                  <TableCell align="center">Attendance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedSubjectData.students.map((student) => {
                  const attendanceRecord = attendanceRecords.find(r => r.studentId === student.id);
                  const isPresent = attendanceRecord?.present || false;
                  
                  return (
                    <TableRow key={student.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            {student.name.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="body1" fontWeight="medium">
                              {student.name}
                            </Typography>
                            {student.email && (
                              <Typography variant="caption" color="textSecondary">
                                {student.email}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={student.rollNumber}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={(student as any).originalClassName || 'Unknown'}
                          size="small"
                          color="info"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                          <Button
                            variant={isPresent ? "contained" : "outlined"}
                            color="success"
                            size="small"
                            onClick={() => handleAttendanceChange(student.id, true)}
                            startIcon={<CheckIcon />}
                          >
                            Present
                          </Button>
                          <Button
                            variant={!isPresent ? "contained" : "outlined"}
                            color="error"
                            size="small"
                            onClick={() => handleAttendanceChange(student.id, false)}
                            startIcon={<CloseIcon />}
                          >
                            Absent
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Empty State */}
      {!selectedSubject && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <SchoolIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Select a subject to start marking attendance
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Choose a subject from the dropdown above to view enrolled students
          </Typography>
        </Paper>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>Confirm Attendance</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to save attendance for {selectedSubjectData?.name} on{' '}
            {new Date(selectedDate).toLocaleDateString()}?
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Present: {presentCount} students
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Absent: {totalCount - presentCount} students
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>Cancel</Button>
          <Button onClick={confirmSaveAttendance} variant="contained">
            Save Attendance
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        message={snackbar.message}
      />
    </Box>
  );
} 